import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Paperclip, Send } from 'lucide-react';

import { EmojiPickerButton } from '@/components/shared/emoji-picker-button';
import { VariableInsertMenu } from '@/components/shared/variables/variable-insert-menu';
import type { IVariableGroup } from '@/components/shared/variables/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    BUG_REPORT_ACCEPT,
    MAX_BUG_REPORT_ATTACHMENTS,
    MAX_BUG_REPORT_COMMENT_LENGTH,
} from '../constants';
import { useBugReportAttachmentUpload } from '../hooks/use-bug-report-attachment-upload';
import type { IBugReportAttachmentInput } from '../types';
import { resolveVariables } from '../utils';
import { PendingAttachmentList } from './pending-attachment-list';

interface IReportComposerProps {
    /** Merge tags offered by the Variables menu, grouped for the dropdown. */
    readonly variables: readonly IVariableGroup[];
    /** token → the text it resolves to when the reply is sent. */
    readonly variableValues: Record<string, string>;
    readonly isSending: boolean;
    /**
     * Resolves when the reply is accepted, which is when the draft and the
     * upload queue are cleared. A rejection leaves both in place so the reply
     * can be retried rather than retyped — the owner surfaces the error.
     */
    readonly onSend: (
        body: string,
        attachments: readonly IBugReportAttachmentInput[],
    ) => Promise<unknown>;
}

/**
 * The reply box for one bug-report thread: textarea, then a toolbar of emoji ·
 * attach · Variables on the left and Send on the right.
 *
 * Deliberately self-contained — it owns the draft and the upload queue — so the
 * super admin portal's composer can mirror it control for control. The two ends
 * of one thread must not disagree about what Enter does or what can be attached.
 */
export function ReportComposer({
    variables,
    variableValues,
    isSending,
    onSend,
}: IReportComposerProps) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attach = useBugReportAttachmentUpload();

    const body = draft.trim();
    const isFull = attach.items.length >= MAX_BUG_REPORT_ATTACHMENTS;
    // A screenshot on its own is a complete reply — "here, look at this" — so
    // either a body or a landed file is enough to send. An upload still in
    // flight blocks Send rather than being dropped from it: the reply would
    // otherwise arrive claiming proof that is not in the bucket.
    const canSubmit =
        (body.length > 0 || attach.uploaded.length > 0) &&
        body.length <= MAX_BUG_REPORT_COMMENT_LENGTH &&
        !isSending &&
        !attach.isUploading;

    /** Splice text in at the caret and leave the cursor after it. */
    const insertAtCursor = useCallback((text: string) => {
        const el = textareaRef.current;
        if (!el) {
            setDraft((prev) => prev + text);
            return;
        }
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        setDraft((prev) => prev.slice(0, start) + text + prev.slice(end));
        requestAnimationFrame(() => {
            const node = textareaRef.current;
            if (!node) return;
            node.focus();
            const caret = start + text.length;
            node.setSelectionRange(caret, caret);
        });
    }, []);

    async function submit() {
        if (!canSubmit) return;
        try {
            // `uploaded` skips anything still in flight or failed, so a reply
            // never claims proof that is not in the bucket.
            await onSend(resolveVariables(body, variableValues), attach.uploaded);
            setDraft('');
            attach.clear();
        } catch {
            // Left as typed — the owner's mutation already toasted the failure.
        }
    }

    return (
        // One bordered surface holding the textarea AND its toolbar, rather
        // than a field with controls floating beneath it: the buttons act on
        // what is being typed, so they should read as part of the same control.
        // The border lives here and is stripped off the textarea below.
        //
        // Because it LOOKS like one field it has to behave like one: `cursor-text`
        // over the whole surface, and a click anywhere in the dead space puts the
        // caret in the textarea. Without that, the padding around a short draft
        // silently swallows clicks that were plainly aimed at the input.
        <div
            className="flex cursor-text flex-col rounded-lg border border-border-default bg-bg-surface transition-colors focus-within:border-primary-300 focus-within:shadow-[0_0_0_2px_var(--color-primary-100)] dark:focus-within:border-primary-600 dark:focus-within:shadow-[0_0_0_2px_var(--color-primary-900)]"
            onMouseDown={(event) => {
                // Only the container's own dead space — a mousedown that
                // started on the textarea, a button or the picker must keep
                // its own behaviour (drag-select, click, open).
                if (event.target !== event.currentTarget) return;
                event.preventDefault();
                textareaRef.current?.focus();
            }}
        >
            <Textarea
                ref={textareaRef}
                rows={2}
                maxLength={MAX_BUG_REPORT_COMMENT_LENGTH}
                className="resize-none rounded-none border-0 bg-transparent px-3 pt-2 pb-1 text-xs-lg shadow-none hover:border-0 focus-visible:border-0 focus-visible:shadow-none disabled:bg-transparent dark:bg-transparent dark:hover:border-0 dark:focus-visible:border-0 dark:focus-visible:shadow-none"
                placeholder={t('bugReports.thread.placeholder', 'Write a reply…')}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                // Chat convention, matched exactly by the super admin's
                // composer — the two ends of one thread must not disagree
                // about what Enter does. Shift+Enter is the newline.
                //
                // `isComposing` guards the IME: mid-composition Enter commits
                // the candidate word, and sending there would fire on a
                // half-typed message in every language that uses one.
                onKeyDown={(event) => {
                    if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing
                    ) {
                        event.preventDefault();
                        void submit();
                    }
                }}
                aria-label={t('bugReports.thread.replyLabel', 'Reply to this report')}
            />

            <div className="px-2 empty:hidden">
                <PendingAttachmentList items={attach.items} onRemove={attach.remove} />
            </div>

            {/* `cursor-default` resets the container's `cursor-text` — the
                toolbar is not typing surface, and an I-beam over a button
                reads as "you can select this", not "you can press it". */}
            <div className="flex cursor-default flex-wrap items-center justify-between gap-2 px-2 pt-1 pb-2">
                <div className="flex items-center gap-1">
                    <EmojiPickerButton onSelect={insertAtCursor} disabled={isSending} />

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={BUG_REPORT_ACCEPT}
                        className="hidden"
                        onChange={(event) => {
                            const { files } = event.target;
                            if (files && files.length > 0) attach.addFiles(files);
                            // Cleared so re-picking the same file fires `change`
                            // again — otherwise a removed attachment cannot be
                            // put back without choosing a different file first.
                            event.target.value = '';
                        }}
                    />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="text-text-secondary"
                                disabled={isSending || isFull}
                                aria-label={t('bugReports.thread.attach', 'Attach a file')}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Paperclip className="size-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {isFull
                                ? t(
                                      'bugReports.form.tooManyFiles',
                                      'You can attach up to {{count}} files',
                                      { count: MAX_BUG_REPORT_ATTACHMENTS },
                                  )
                                : t('bugReports.thread.attach', 'Attach a file')}
                        </TooltipContent>
                    </Tooltip>

                    <VariableInsertMenu
                        groups={variables}
                        onInsert={(token) => insertAtCursor(`{{${token}}}`)}
                        disabled={isSending}
                    />
                </div>

                {/* Send sits on the toolbar row rather than under its own hint
                    line: the Enter/Shift+Enter convention is the same one every
                    chat box uses, and spending a second row restating it pushed
                    the thread above out of view in a dialog this size. The
                    textarea's placeholder and this button carry it instead. */}
                <Button type="button" size="sm" disabled={!canSubmit} onClick={() => void submit()}>
                    {isSending ? (
                        <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                        <Send className="size-3.5" />
                    )}
                    {t('bugReports.thread.send', 'Send reply')}
                </Button>
            </div>
        </div>
    );
}
